import { computed, effect, inject, untracked } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

import { TranslateService } from '@ngx-translate/core';
import { pipe, switchMap, tap } from 'rxjs';
import {
  patchState, signalStore, withComputed, withHooks, withMethods, withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  AppError, setError, setPending, setSuccess, SpinnerService, withFiltersUpdate, withRequestHandler,
  withRequestStatus,
} from '@hglobal/common';
import { Transaction, TransactionDirection } from '@pt/models';

import { TRANSACTIONS_API } from './api';
import {
  GetMetricsReq, GetTransactionsReq, TransactionsFilters, TransactionsState,
} from './models';

export const initialFilters: TransactionsFilters = {
  page: 1,
  limit: 12,
  period: null,
  metric: null,
  direction: null,
};

const initialState: TransactionsState = {
  filters: initialFilters,
  metrics: [],
  charts: [],
  transactions: [],
  itemsCount: 0,
};

export const TransactionsStore = signalStore(
  withState(initialState),
  withComputed(({ transactions, metrics }) => ({
    incomeMetrics: computed(() => metrics().filter(
      metric => metric.direction === TransactionDirection.Income,
    )),
    expenseMetrics: computed(() => metrics().filter(
      metric => metric.direction === TransactionDirection.Expense,
    )),
    tableTransactions: computed(() => new MatTableDataSource<Transaction>(transactions())),
  })),
  withRequestStatus(),
  withRequestHandler(),
  withFiltersUpdate<TransactionsFilters>(),
  withMethods(store => {
    const translateService = inject(TranslateService);
    const spinnerService = inject(SpinnerService);
    const apiService = inject(TRANSACTIONS_API);

    return {
      getMetrics: rxMethod<GetMetricsReq>(
        pipe(
          tap(() => patchState(store, setPending())),
          switchMap(params => spinnerService.observe(apiService.getMetrics(params), true)
            .pipe(
              tapResponse({
                next: metrics => {
                  patchState(store, {
                    metrics: metrics.map(metric => ({
                      ...metric,
                      name: translateService.instant(`transactions.metrics.${metric.id}`),
                    })),
                  }, setSuccess());
                },
                error: (error: AppError) => patchState(store, setError(error)),
              }),
            )),
        ),
      ),

      getTransactions: rxMethod<GetTransactionsReq>(
        pipe(
          tap(() => patchState(store, setPending())),
          switchMap(params => spinnerService.observe(apiService.getTransactions(params), true)
            .pipe(
              tapResponse({
                next: ({ charts, transactions, itemsCount }) => {
                  patchState(store, { charts, transactions, itemsCount }, setSuccess());
                },
                error: (error: AppError) => patchState(store, setError(error)),
              }),
            )),
        ),
      ),
    };
  }),
  withHooks({
    onInit(store) {
      effect(() => {
        if (store.error()) {
          untracked(() => store.handleError(store.error()));
        }
      });
    },
  }),
);
